import React, { useRef } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import Button from '@material-ui/core/Button';
import hiaLogo from '../../images/logoHIAsecure.png';
import ConventionThemeScope from './theme/ConventionThemeScope';
import useConventionPage from './useConventionPage';
import { VARIANTS } from './displayVariant';
import SecretSection from './sections/SecretSection';
import HowItWorksSection from './sections/HowItWorksSection';
import ExamplesSection from './sections/ExamplesSection';
import PracticeSection from './sections/PracticeSection';
import ValidationSection from './sections/ValidationSection';
import StickyValidateBar, { useInView } from './StickyValidateBar';
import SessionCountdown from './SessionCountdown';
import SessionExpiredNotice from './SessionExpiredNotice';

// A real <button> (focus visible, full width on mobile): the only way to start the session.
const StartButton = ({ onStart }) => (
    <Button
        variant="contained"
        color="primary"
        className="hia-ack-validate hia-ack-start"
        onClick={onStart}
        data-testid="intro-start"
    >
        <FormattedMessage id="conventionPage.intro.start" />
    </Button>
);

const Header = ({ intl, countdown }) => (
    <header className="hia-ack-header">
        <img src={hiaLogo} className="hia-ack-logo" alt={intl.formatMessage({ id: 'conventionPage.logoAlt' })} />
        <h1 className="hia-ack-h1"><FormattedMessage id="conventionPage.title" /></h1>
        {countdown}
    </header>
);

// The single "Your convention" page of the acknowledgement link: what you look for, how it works, examples,
// practice, then the validation at the bottom. No other navigation.
export const ConventionPage = ({ ackToken, language, api, intl }) => {
    const { state, start, check, another, validate, expire } = useConventionPage({ ackToken, language, api });
    const validationRef = useRef(null);
    const validationInView = useInView(validationRef, state.status === 'ready');

    if (state.status === 'intro') {
        // Start gate (HLvgihbj E1): nothing is fetched yet — the single ack-start of the visit fires on the
        // click. No read-only route exposes the tenant session duration before ack-start (BFF whitelist), so
        // the wording stays generic ("a limited time") rather than hard-coding a number.
        return (
            <ConventionThemeScope>
                <main className="hia-ack-page">
                    <Header intl={intl} />
                    <section className="hia-ack-card hia-ack-card--validate" aria-labelledby="hia-ack-h-intro" data-section="intro" data-testid="intro">
                        <h2 id="hia-ack-h-intro" className="hia-ack-h2"><FormattedMessage id="conventionPage.intro.heading" /></h2>
                        <p>
                            <FormattedMessage
                                id="conventionPage.intro.body"
                                values={{ duration: intl.formatMessage({ id: 'conventionPage.intro.duration.limited' }) }}
                            />
                        </p>
                        <p><FormattedMessage id="conventionPage.intro.calm" /></p>
                        <StartButton onStart={start} />
                    </section>
                </main>
            </ConventionThemeScope>
        );
    }

    if (state.status === 'loading') {
        return (
            <ConventionThemeScope>
                <main className="hia-ack-page" aria-busy="true">
                    <Header intl={intl} />
                    <p className="hia-ack-note" role="status"><FormattedMessage id="conventionPage.loading" /></p>
                </main>
            </ConventionThemeScope>
        );
    }

    if (state.status === 'failed') {
        return (
            <ConventionThemeScope>
                <main className="hia-ack-page">
                    <Header intl={intl} />
                    <div className="hia-ack-alert" role="alert" data-testid="start-error">
                        <p className="hia-ack-alert-title"><FormattedMessage id="conventionPage.error.title" /></p>
                        <p><FormattedMessage id={state.startErrorId} /></p>
                    </div>
                </main>
            </ConventionThemeScope>
        );
    }

    if (state.expired) {
        // At expiry the convention content leaves the DOM entirely (HLvgihbj E3): only the "time is up"
        // notice remains, with the tenant link rules kept apart in `ackSettings`.
        return (
            <ConventionThemeScope>
                <main className="hia-ack-page">
                    <Header intl={intl} />
                    <SessionExpiredNotice ackSettings={state.ackSettings} />
                </main>
            </ConventionThemeScope>
        );
    }

    const { convention, variant, acknowledged } = state;
    const parameters = (convention && convention.conventionParameters) || {};
    const showCountdown = !acknowledged && Number.isFinite(state.sessionEnd);
    const countdown = showCountdown ? <SessionCountdown sessionEnd={state.sessionEnd} onExpire={expire} /> : null;
    const practiceActive = variant === VARIANTS.SECRET || variant === VARIANTS.COPY;

    return (
        <ConventionThemeScope>
            <main className="hia-ack-page">
                <Header intl={intl} countdown={countdown} />
                <SecretSection variant={variant} parameters={parameters} />
                {convention && <HowItWorksSection variant={variant} parameters={parameters} examples={state.examples} />}
                {practiceActive && <ExamplesSection examples={state.examples} blockSizes={parameters.blocks} />}
                {practiceActive && (
                    <PracticeSection
                        exercise={state.exercise}
                        score={state.score}
                        parameters={parameters}
                        disabled={false}
                        acknowledged={acknowledged}
                        onCheck={check}
                        onAnother={another}
                    />
                )}
                <StickyValidateBar
                    visible={!validationInView && !acknowledged}
                    countdown={countdown}
                    pending={state.validation.pending}
                    onValidate={validate}
                />
                <ValidationSection
                    acknowledged={acknowledged}
                    expired={false}
                    validation={state.validation}
                    onValidate={validate}
                    sectionRef={validationRef}
                />
            </main>
        </ConventionThemeScope>
    );
};

export default injectIntl(ConventionPage);
